import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Icon, COLORS } from "@treelab/panda";

import { ChartErrorType, ChartType } from "../../types/global-type";

import { pieLimitError } from "../../constants/svg";
import { PIE_DIMENSION_LIMIT, RING_PIE_SMALL_CIRCLE_RADIUS, IS_PIE_CHART } from "../../constants/chart";

import { Container, PieCountError, EmptyAxis, ZoneContainer, AxisMessage, EmptyPie, PieMessage, Pie } from "./styled";

interface IProps {
  errorType: ChartErrorType;
  chartType: ChartType;
  isExpand: boolean;
}

const ShowAxis: { [key in ChartType]: boolean } = {
  [ChartType.BASIC_BAR]: true,
  [ChartType.STACKED_COLUMN_BAR]: true,
  [ChartType.BASIC_LINE]: true,
  [ChartType.PIE_RING]: false,
  [ChartType.BASIC_PIE]: false,
};

const ShowPie: { [key in ChartType]: boolean } = {
  [ChartType.BASIC_BAR]: false,
  [ChartType.STACKED_COLUMN_BAR]: false,
  [ChartType.BASIC_LINE]: false,
  [ChartType.PIE_RING]: true,
  [ChartType.BASIC_PIE]: true,
};

const ChartError: React.FC<IProps> = ({ errorType, chartType, isExpand }) => {
  const emptyPie = useRef<HTMLDivElement>(null);
  const [pieDiameter, setPieDiameter] = useState<number>(0);

  const refreshDiameter = useCallback(() => {
    if (!IS_PIE_CHART[chartType]) return;
    if (emptyPie && emptyPie.current) {
      const width = emptyPie.current.clientWidth;
      const height = emptyPie.current.clientHeight;
      setPieDiameter(width > height ? height : width);
    }
  }, [emptyPie, chartType]);

  useEffect(() => {
    if (emptyPie) {
      refreshDiameter();
    }
  }, [emptyPie, refreshDiameter]);

  useEffect(() => {
    window.addEventListener("resize", refreshDiameter, false);
    return () => {
      window.removeEventListener("resize", refreshDiameter, false);
    };
  }, [refreshDiameter]);

  switch (errorType) {
    case ChartErrorType.PIE_DIMENSION_COUNT_LIMIT:
      return (
        <Container>
          <PieCountError>
            <Icon icon={pieLimitError} size={{ width: 200, height: 120 }} />
            <FormattedMessage id="error.chart.pie.limit" values={{ count: PIE_DIMENSION_LIMIT }} />
          </PieCountError>
        </Container>
      );
    case ChartErrorType.SOME_FIELDS_ARE_INVALID:
    case ChartErrorType.SOME_FIELDS_ARE_UNDEFINED:
    case ChartErrorType.DATASET_IS_EMPTY_ARRAY:
      const isShowMessage = errorType === ChartErrorType.SOME_FIELDS_ARE_INVALID;
      return (
        <Container>
          {ShowAxis[chartType] && (
            <EmptyAxis isExpand={isExpand}>
              <ZoneContainer>0</ZoneContainer>
            </EmptyAxis>
          )}
          {ShowAxis[chartType] && isShowMessage && (
            <AxisMessage isExpand={isExpand}>
              <FormattedMessage id="error.chart.null" />
            </AxisMessage>
          )}
          {ShowPie[chartType] && (
            <EmptyPie isExpand={isExpand} ref={emptyPie}>
              <Pie diameter={pieDiameter} color={COLORS.greyLight5}>
                {chartType === ChartType.PIE_RING && (
                  <Pie diameter={pieDiameter * RING_PIE_SMALL_CIRCLE_RADIUS} color={COLORS.white}></Pie>
                )}
              </Pie>
              {isShowMessage && (
                <PieMessage>
                  <FormattedMessage id="error.chart.null" />
                </PieMessage>
              )}
            </EmptyPie>
          )}
        </Container>
      );
    default:
      return <div />;
  }
};

export default ChartError;
